import { createEffect, createEvent, createStore, sample } from 'effector'
import { versioning } from '~/core/flags'
import { model as router } from '~/core/router'
import {
  $locale,
  $localeChanged,
  dateLanguageChanged,
  languageChange,
  remoteLanguageReceived,
  updateLanguageFx,
} from './main'

export const LOCALE_STORAGE_KEY = 'tria__lang'
export const SERIES_PLAYLIST_STORAGE_KEY = 'tria__series-playlist'

const AUTH_PATH_PREFIXES = [
  '/sign-in',
  '/sign-up',
  '/toa',
  '/geoblock',
  '/expired',
  '/subscription-status',
  '/logout',
  '/forgot-password',
  '/fail',
] as const

export function isAuthRoute(pathname: string | null | undefined): boolean {
  return (
    !!pathname &&
    AUTH_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix))
  )
}

/**
 * Persist selected locale (if provided), clear localized playlist, reload.
 *
 * `$locale` is already backed by effector-storage `persist` in `main.ts`.
 * This sync write flushes the pending reload locale before `location.reload()`
 * so a same-tick persist cannot lose the race. Playlist key is not on that
 * persist, so `removeItem` stays imperative. Cross-tab uses native `storage`
 * (below) instead of effector-storage live-sync so MW ≥ 3.24 reloads rather
 * than applying `$locale → languageChange`.
 */
export const reloadAfterLanguageChangeFx = createEffect(
  (locale: string | null) => {
    if (locale != null) {
      try {
        localStorage.setItem(LOCALE_STORAGE_KEY, JSON.stringify(locale))
      } catch {
        // ignore storage errors
      }
    }
    try {
      localStorage.removeItem(SERIES_PLAYLIST_STORAGE_KEY)
    } catch {
      // ignore storage errors
    }
    window.location.reload()
  }
)

/** Latest locale that should trigger page reload after i18n apply / persist */
export const $pendingLanguageReload = createStore<string | null>(null).reset(
  reloadAfterLanguageChangeFx
)

/**
 * Locale changed in another tab before `$wbsVersion` was known.
 * Resolved once version arrives: reload on MW ≥ 3.24, drop on older MW.
 */
export const $pendingCrossTabReload = createStore(false).reset(
  reloadAfterLanguageChangeFx
)

/** Native `storage` event from another tab (same-tab writes do not emit it) */
export const localeStorageChanged = createEvent<StorageEvent>()

/** App launch binds the native storage listener once. */
export const setupContentLocalization = createEvent()

const bindLocaleStorageListenerFx = createEffect(() => {
  if (typeof window === 'undefined') return

  const onStorage = (event: StorageEvent) => {
    if (event.key === LOCALE_STORAGE_KEY && event.newValue != null) {
      localeStorageChanged(event)
    }
  }

  window.addEventListener('storage', onStorage)
})

sample({
  clock: setupContentLocalization,
  target: bindLocaleStorageListenerFx,
})

// Capture picks that will reload (capability on, or version still unknown).
sample({
  clock: languageChange,
  source: {
    locale: $locale,
    version: versioning.$wbsVersion,
    enabled: versioning.$isContentLocalizationEnabled,
    pathname: router.$pathname,
  },
  filter: ({ locale, version, enabled, pathname }, { id }) =>
    id !== locale && (version === null || enabled) && !isAuthRoute(pathname),
  fn: (_, { id }) => id,
  target: $pendingLanguageReload,
})

// Apply i18n only when we will NOT reload (legacy MW, or auth routes — FP-4219).
// Skipping apply on the reload path avoids a mixed UI flash: new $t labels +
// old API metadata (FP-4220).
// Do not filter `id === locale`: legacy `$locale → languageChange` sync fires
// after `$locale` already matches the pick.
sample({
  clock: languageChange,
  source: {
    version: versioning.$wbsVersion,
    enabled: versioning.$isContentLocalizationEnabled,
    pathname: router.$pathname,
  },
  filter: ({ version, enabled, pathname }) =>
    isAuthRoute(pathname) || (version !== null && !enabled),
  fn: (_, { id }) => id,
  target: updateLanguageFx,
})

// Reload immediately after pending is set when capability is known and on.
// Must stay after the pending sample; do not wait for dateLanguageChanged.
// Auth routes must not reload (FP-4219).
sample({
  clock: languageChange,
  source: {
    locale: $locale,
    version: versioning.$wbsVersion,
    enabled: versioning.$isContentLocalizationEnabled,
    pathname: router.$pathname,
  },
  filter: ({ locale, version, enabled, pathname }, { id }) =>
    id !== locale && version !== null && enabled && !isAuthRoute(pathname),
  fn: (_, { id }) => id,
  target: reloadAfterLanguageChangeFx,
})

// Remote bootstrap still applies i18n first; reload once date-fns/locale catch up.
sample({
  clock: dateLanguageChanged,
  source: {
    pending: $pendingLanguageReload,
    version: versioning.$wbsVersion,
    enabled: versioning.$isContentLocalizationEnabled,
    pathname: router.$pathname,
  },
  filter: ({ pending, version, enabled, pathname }, locale) =>
    pending != null &&
    pending === locale &&
    version !== null &&
    enabled &&
    !isAuthRoute(pathname),
  fn: ({ pending }) => pending,
  target: reloadAfterLanguageChangeFx,
})

// Early current-tab pick: version arrived with pending locale and capability on → reload
sample({
  clock: versioning.$wbsVersion,
  source: {
    pending: $pendingLanguageReload,
    enabled: versioning.$isContentLocalizationEnabled,
    pathname: router.$pathname,
  },
  filter: ({ pending, enabled, pathname }, version) =>
    pending != null && version !== null && enabled && !isAuthRoute(pathname),
  fn: ({ pending }) => pending,
  target: reloadAfterLanguageChangeFx,
})

// Early current-tab pick: version arrived with capability off → live-apply then drop pending
sample({
  clock: versioning.$wbsVersion,
  source: {
    pending: $pendingLanguageReload,
    enabled: versioning.$isContentLocalizationEnabled,
  },
  filter: ({ pending, enabled }, version) =>
    pending != null && version !== null && !enabled,
  fn: ({ pending }) => pending as string,
  target: updateLanguageFx,
})

sample({
  clock: versioning.$wbsVersion,
  source: {
    pending: $pendingLanguageReload,
    enabled: versioning.$isContentLocalizationEnabled,
  },
  filter: ({ pending, enabled }, version) =>
    pending != null && version !== null && !enabled,
  fn: () => null,
  target: $pendingLanguageReload,
})

// Neighbor tab: locale key changed while version is still unknown → defer
sample({
  clock: localeStorageChanged,
  source: versioning.$wbsVersion,
  filter: (version) => version === null,
  fn: () => true,
  target: $pendingCrossTabReload,
})

// Neighbor tab: capability already known and on → reload now (locale already in storage)
sample({
  clock: localeStorageChanged,
  source: {
    enabled: versioning.$isContentLocalizationEnabled,
    pathname: router.$pathname,
  },
  filter: ({ enabled, pathname }) => enabled && !isAuthRoute(pathname),
  fn: () => null,
  target: reloadAfterLanguageChangeFx,
})

// Deferred cross-tab: version arrived with capability on → reload once
sample({
  clock: versioning.$wbsVersion,
  source: {
    pending: $pendingCrossTabReload,
    enabled: versioning.$isContentLocalizationEnabled,
    pathname: router.$pathname,
  },
  filter: ({ pending, enabled, pathname }, version) =>
    pending && version !== null && enabled && !isAuthRoute(pathname),
  fn: () => null,
  target: reloadAfterLanguageChangeFx,
})

// Deferred cross-tab: version arrived with capability off → drop pending (legacy live-sync)
sample({
  clock: versioning.$wbsVersion,
  source: {
    pending: $pendingCrossTabReload,
    enabled: versioning.$isContentLocalizationEnabled,
  },
  filter: ({ pending, enabled }, version) =>
    pending && version !== null && !enabled,
  fn: () => false,
  target: $pendingCrossTabReload,
})

// On MW ≥ 3.24, reload so content / mw-language match the remote default locale.
// Remote bootstrap does not set `$localeChanged` (not a user pick).
sample({
  clock: remoteLanguageReceived,
  source: {
    locale: $locale,
    changed: $localeChanged,
    version: versioning.$wbsVersion,
    enabled: versioning.$isContentLocalizationEnabled,
    pathname: router.$pathname,
  },
  filter: ({ changed, locale, version, enabled, pathname }, remote) =>
    !changed &&
    remote != null &&
    remote !== '' &&
    remote !== locale &&
    (version === null || enabled) &&
    !isAuthRoute(pathname),
  fn: (_, remote) => remote as string,
  target: $pendingLanguageReload,
})
